import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import type { RespondedType, ResponseError } from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useBanGroupMember(groupId: number | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, BanGroupMemberRequest>({
    mutationKey: ['/BanGroupUser', groupId],
    mutationFn: (params) =>
      fetcher(
        '/BanGroupUser',
        {
          GroupID: groupId,
          UserID: params.userId,
          Status: params.ban === 'ban' ? '0' : '1',
        },
        { validResponseCodes: [0] },
      ),
    onSuccess: (_, params) => {
      const queryOptions = generateGroupInfoQueryOptions(groupId);

      queryClient.setQueryData(queryOptions.queryKey, (data) =>
        produce(data, (draft) => {
          if (!draft?.Members) return draft;

          const member = draft.Members.find((m) => m.UserID === params.userId);

          if (!member) return draft;

          member.Status = params.ban === 'ban' ? 0 : 1;
        }),
      );
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}

type BanGroupMemberRequest = {
  userId: number;
  ban: 'ban' | 'unban';
};
